/**
 * Time helpers. Everything takes an explicit epoch (ms) and an IANA time zone,
 * so callers inject the clock. A game day starts at 04:00 local time.
 */

export const DAY_START_HOUR = 4;
export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;

/** Injected clock. Production uses Date.now, tests and soak use fakes. */
export interface Clock {
  now(): number;
}
export const systemClock: Clock = { now: () => Date.now() };

/** Game day key, e.g. "2026-10-17". */
export type DayKey = string;

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

// Formatters are expensive to create; one per zone, bounded by the number of zones in use.
const formatters = new Map<string, Intl.DateTimeFormat>();
const MAX_FORMATTERS = 8;

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    if (formatters.size >= MAX_FORMATTERS) formatters.clear();
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      weekday: "short",
    });
    formatters.set(tz, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function zonedParts(ms: number, tz: string): ZonedParts {
  const out: ZonedParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, weekday: 0 };
  for (const p of formatter(tz).formatToParts(new Date(ms))) {
    switch (p.type) {
      case "year":
        out.year = Number(p.value);
        break;
      case "month":
        out.month = Number(p.value);
        break;
      case "day":
        out.day = Number(p.value);
        break;
      case "hour":
        out.hour = Number(p.value) % 24;
        break;
      case "minute":
        out.minute = Number(p.value);
        break;
      case "second":
        out.second = Number(p.value);
        break;
      case "weekday":
        out.weekday = WEEKDAYS[p.value] ?? 0;
        break;
    }
  }
  return out;
}

const pad = (n: number) => String(n).padStart(2, "0");

export function keyFromYmd(y: number, m: number, d: number): DayKey {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function parseDayKey(key: DayKey): { year: number; month: number; day: number } {
  const [y, m, d] = key.split("-").map(Number);
  return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 };
}

export function isDayKey(s: unknown): s is DayKey {
  return (
    typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + "T00:00:00Z"))
  );
}

/** Calendar arithmetic on day keys (zone independent). */
export function addDays(key: DayKey, n: number): DayKey {
  const { year, month, day } = parseDayKey(key);
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return keyFromYmd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function daysBetween(a: DayKey, b: DayKey): number {
  const pa = parseDayKey(a);
  const pb = parseDayKey(b);
  return Math.round(
    (Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) / DAY,
  );
}

/** 0 = Sunday. */
export function weekdayOfKey(key: DayKey): number {
  const { year, month, day } = parseDayKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Monday of the week containing the given game day. */
export function weekKey(key: DayKey): DayKey {
  const wd = weekdayOfKey(key);
  return addDays(key, -((wd + 6) % 7));
}

/** Game day for an instant: before 04:00 local counts to the previous calendar day. */
export function dayKey(ms: number, tz: string): DayKey {
  const p = zonedParts(ms, tz);
  const k = keyFromYmd(p.year, p.month, p.day);
  return p.hour < DAY_START_HOUR ? addDays(k, -1) : k;
}

/** Minutes since local midnight. */
export function localMinutes(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  return p.hour * 60 + p.minute;
}

/**
 * Epoch for a local wall-clock time. For times that do not exist (spring forward gap)
 * the result is shifted forward by the gap; ambiguous times (fall back) resolve to the first one.
 */
export function zonedToEpoch(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  tz: string,
): number {
  const target = Date.UTC(year, month - 1, day, hour, minute);
  const localAsUtc = (t: number) => {
    const p = zonedParts(t, tz);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  };
  // Offsets on both sides of a possible transition near the target.
  const offsets = [
    ...new Set([
      localAsUtc(target - 14 * HOUR) - (target - 14 * HOUR),
      localAsUtc(target + 14 * HOUR) - (target + 14 * HOUR),
    ]),
  ];
  const valid = offsets.map((o) => target - o).filter((t) => localAsUtc(t) === target);
  if (valid.length) return Math.min(...valid);
  // Gap (spring forward): use the pre-transition offset, which lands after the gap.
  return target - Math.min(...offsets);
}

/** "HH:MM" -> minutes since midnight, or null. */
export function parseHm(hm: string): number | null {
  const m = /^(\d{1,2})(?::(\d{2}))?$/.exec(hm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function formatHm(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/**
 * Epoch for a wall-clock time that belongs to a game day.
 * Times before 04:00 belong to the night after the calendar date of the key.
 */
export function atGameDay(key: DayKey, minutes: number, tz: string): number {
  const cal = minutes < DAY_START_HOUR * 60 ? addDays(key, 1) : key;
  const { year, month, day } = parseDayKey(cal);
  return zonedToEpoch(year, month, day, Math.floor(minutes / 60), minutes % 60, tz);
}

/** Start of the given game day (04:00 local). */
export function gameDayStart(key: DayKey, tz: string): number {
  return atGameDay(key, DAY_START_HOUR * 60, tz);
}

/** The next 04:00 after `ms`. */
export function nextDayStart(ms: number, tz: string): number {
  return gameDayStart(addDays(dayKey(ms, tz), 1), tz);
}

export function formatLocal(ms: number, tz: string): string {
  const p = zonedParts(ms, tz);
  return `${keyFromYmd(p.year, p.month, p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}

export const WEEKDAY_NAMES_PL = [
  "niedziela",
  "poniedziałek",
  "wtorek",
  "środa",
  "czwartek",
  "piątek",
  "sobota",
] as const;
